import type { ReactNode } from 'react';
import {
  NOTE_ROLES,
  NOTE_ROLE_PLURAL,
  type PracticeNoteSettings,
  type PracticeNoteSettingsInput,
} from '@ehr/shared';

/**
 * ehr#682 (PROGRESS-NOTE-10-20-V141 drawNoteSettingsBody, V126 / V133 / V135 / V136): Note
 * settings > Practice note settings, shown to admins, managers and supervisors. Saved with the
 * window's Save (PUT /practice/note-settings), for every note in the practice.
 *
 * V141 keeps AI per role in its data (aiByRole) without a row for it; the row "Generate notes with
 * AI for associates" is that setting, so a supervisor can clear their associates for AI.
 *
 * ehr#725 (CASE_MANAGEMENT_NOTE_10-30-V9): on a case management note, "Show treatment plan goals"
 * (20 A, off by default), with the goal rule ("At least one problem, goal, or objective ...") only
 * while it is ticked (V5: off hides the goal rules too), and the rows the note adds below (its
 * Default billing code, 14 A).
 */
export function PracticeNoteSettingsSection({
  value,
  onChange,
  goalRules = true,
  caseGoals = false,
  children,
}: {
  value: PracticeNoteSettings;
  onChange: (v: PracticeNoteSettings) => void;
  /** ehr#724: false leaves out the problems, goals and objectives rule (an Admin / phone note). */
  goalRules?: boolean;
  /** ehr#725: show the Show treatment plan goals switch (a case management note's settings). */
  caseGoals?: boolean;
  /** ehr#725: rows of the note's own, at the end of the section. */
  children?: ReactNode;
}) {
  const set = (o: Partial<PracticeNoteSettings>) => onChange({ ...value, ...o });
  return (
    <div className="pn-ns-sec" data-test="ns-practice">
      <div className="pn-ns-h">
        <b>Practice note settings</b>
        <span className="pn-ns-muted">Admins and supervisors</span>
      </div>
      <div className="pn-ns-h pn-ns-row">
        <span>Generate notes with AI for associates</span>
        <span className="pn-sp" />
        <div
          className="pn-seg pn-ns-seg"
          role="group"
          aria-label="Generate notes with AI for associates"
        >
          {(
            [
              [true, 'On'],
              [false, 'Off'],
            ] as const
          ).map(([v, t]) => (
            <button
              key={t}
              type="button"
              aria-pressed={value.aiForAssociates === v}
              onClick={() => set({ aiForAssociates: v })}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <div className="pn-ns-sws">
        <label className="pn-ns-sw">
          <input
            type="checkbox"
            checked={value.allowSkipRequired}
            onChange={(e) => set({ allowSkipRequired: e.target.checked })}
          />
          Allow skipping required boxes at sign-off
        </label>
        {caseGoals && (
          <label className="pn-ns-sw" data-test="cm-goals">
            <input
              type="checkbox"
              checked={value.caseManagementGoals}
              onChange={(e) => set({ caseManagementGoals: e.target.checked })}
            />
            Show treatment plan goals
          </label>
        )}
        {/* ehr#725 (V9 V5): on a case management note the goal rule shows only with goals on */}
        {goalRules && (!caseGoals || value.caseManagementGoals) && (
          <label className="pn-ns-sw">
            <input
              type="checkbox"
              checked={value.requireAddressedFocus}
              onChange={(e) => set({ requireAddressedFocus: e.target.checked })}
            />
            At least one problem, goal, or objective must be addressed
          </label>
        )}
        {/* ehr#731 (Jay 10-30 on #713 Q1): Off by default */}
        <label className="pn-ns-sw">
          <input
            type="checkbox"
            checked={value.noShowSupervisorSignoff}
            onChange={(e) => set({ noShowSupervisorSignoff: e.target.checked })}
          />
          Supervisor sign-off on associates&rsquo; no-show / cancellation notes
          <span
            className="pn-tip"
            tabIndex={0}
            role="button"
            aria-label="What this setting does"
            onClick={(e) => e.preventDefault()}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" width="15" height="15">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 11v5" />
              <circle cx="12" cy="7.6" r="0.6" fill="currentColor" stroke="none" />
            </svg>
            <span className="pn-tip-pop" role="tooltip">
              When off, associates sign their own no-show / cancellation notes. A note with Risk
              concern or Leading to discharge ticked still goes to their supervisor.
            </span>
          </span>
        </label>
      </div>
      <div className="pn-ns-keep">
        <span className="pn-ns-keep-h">
          Must Keep or Revert each supervisor edit before submitting
          <span className="pn-tip" tabIndex={0} role="button" aria-label="What this setting does">
            <svg viewBox="0 0 24 24" aria-hidden="true" width="15" height="15">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 11v5" />
              <circle cx="12" cy="7.6" r="0.6" fill="currentColor" stroke="none" />
            </svg>
            <span className="pn-tip-pop" role="tooltip">
              When on, people in this role must Keep or Revert each supervisor edit before they can
              resubmit. When off, resubmitting keeps every open edit.
            </span>
          </span>
        </span>
        <div className="pn-ns-keep-roles">
          {NOTE_ROLES.map((r) => (
            <label key={r} className="pn-ns-sw">
              <input
                type="checkbox"
                aria-label={`${NOTE_ROLE_PLURAL[r]} must Keep or Revert`}
                checked={value.keepRevertByRole[r]}
                onChange={(e) =>
                  set({ keepRevertByRole: { ...value.keepRevertByRole, [r]: e.target.checked } })
                }
              />
              {NOTE_ROLE_PLURAL[r]}
            </label>
          ))}
        </div>
      </div>
      {children}
    </div>
  );
}

/** What changed, for PUT /practice/note-settings (null: nothing). */
export function practiceNotesPatch(
  next: PracticeNoteSettings,
  was: PracticeNoteSettings,
): PracticeNoteSettingsInput | null {
  const out: PracticeNoteSettingsInput = {};
  if (next.aiForAssociates !== was.aiForAssociates) out.aiForAssociates = next.aiForAssociates;
  if (next.allowSkipRequired !== was.allowSkipRequired)
    out.allowSkipRequired = next.allowSkipRequired;
  if (next.requireAddressedFocus !== was.requireAddressedFocus)
    out.requireAddressedFocus = next.requireAddressedFocus;
  if (JSON.stringify(next.keepRevertByRole) !== JSON.stringify(was.keepRevertByRole))
    out.keepRevertByRole = { ...next.keepRevertByRole };
  // ehr#708: the discharge summary's goal rule and its No treatment plan on file reasons
  if (next.requireGoalStatus !== was.requireGoalStatus)
    out.requireGoalStatus = next.requireGoalStatus;
  if (JSON.stringify(next.dischargeNoPlanReasons) !== JSON.stringify(was.dischargeNoPlanReasons))
    out.dischargeNoPlanReasons = [...next.dischargeNoPlanReasons];
  if (next.caseManagementGoals !== was.caseManagementGoals)
    out.caseManagementGoals = next.caseManagementGoals;
  if (next.noShowSupervisorSignoff !== was.noShowSupervisorSignoff)
    out.noShowSupervisorSignoff = next.noShowSupervisorSignoff; // ehr#731
  return Object.keys(out).length ? out : null;
}
